// Roles whose presentation is FLAG are shown as checkboxes, never as roles, and
// never offered to an admin account (a role whose admin_account /
// role_detail.adminAccount is true). Which roles are flags or admin accounts is
// each consuming app's seed data; UM only renders it. The backend triggers enforce
// the rule; the UI hides what would be refused and says what a save will remove.
// Pure functions: unit-tested with node:test (utils/__tests__/roleFlags.node.test.mjs).

// Mirrors constants/constant.js ROLE_PRESENTATION (CRA cannot import outside src;
// parity is asserted by the node test).
export const ROLE_PRESENTATION = Object.freeze({ ROLE: 'ROLE', FLAG: 'FLAG' });

export const isFlagRole = (role) => !!role && role.presentation === ROLE_PRESENTATION.FLAG;

const detailOf = (role) => {
    const d = role && role.role_detail;
    if (typeof d !== 'string') return d || {};
    try {
        return JSON.parse(d) || {};
    } catch (e) {
        return {};
    }
};

// Same inputs and semantics as lib/role-assignment-guard.js isAdminAccount
// (parity-tested): admin_account column (0/1/'1'/true) OR role_detail.adminAccount
// as a JSON boolean true — the predicate the database triggers use.
export const isAdminAccountRole = (role) =>
    !!role && (role.admin_account === true || Number(role.admin_account) === 1 || detailOf(role).adminAccount === true);

/** Split a role list into { roles, flags }, order preserved. */
export const splitRoles = (all) => ({
    roles: (all || []).filter((r) => !isFlagRole(r)),
    flags: (all || []).filter(isFlagRole),
});

/** True when the chosen (non-flag) role ids include an admin-account role. */
export const flagsHidden = (roleIds, all) =>
    (all || []).some((r) => roleIds.includes(r.role_id) && isAdminAccountRole(r));

/** Flag roles among selectedIds that a save would drop because an admin-account role is picked. */
export const flagsToBeRemoved = (selectedIds, all) => {
    if (!flagsHidden(selectedIds, all)) return [];
    return (all || []).filter((r) => isFlagRole(r) && selectedIds.includes(r.role_id));
};

/** Plain-words notice naming the flag role(s) a save will remove; null when none. */
export const flagRemovalNotice = (flags) => {
    if (!flags || !flags.length) return null;
    const noun = flags.length === 1 ? 'flag role' : 'flag roles';
    return `This will remove the ${noun}: ${flags.map((r) => r.role_name).join(', ')}`;
};

/**
 * Optional free-text hint an app's seed puts on a flag (role_detail.requiresRoleHint,
 * or the requires_role_hint column the role-list queries select), e.g. that the flag
 * only takes effect alongside another role. UM shows it; it never interprets it.
 */
export const requiresRoleHint = (role) => {
    if (!role) return null;
    const raw = role.requires_role_hint ?? detailOf(role).requiresRoleHint;
    if (typeof raw !== 'string') return null;
    const text = raw.trim();
    return text || null;
};

/** The submitted set: role picks ∪ checked flags (flags dropped for an admin account), de-duplicated. */
export const mergeRoleIds = (roleIds, flagIds, all) => {
    const flags = flagsHidden(roleIds, all) ? [] : flagIds;
    return [...new Set([...roleIds, ...flags])];
};
